import React, { useMemo } from 'react'
import {
  UserCircleIcon,
  EnvelopeIcon,
  IdentificationIcon,
  ShieldCheckIcon,
  KeyIcon,
  BuildingOfficeIcon,
} from '@heroicons/react/24/outline'
import { useAuth } from '../contexts/AuthContext'
import { ROLE_ID_ADMIN } from '../constants/roleIds'

/**
 * Componente de perfil de usuario
 * Muestra información del usuario desde localStorage con diseño minimalista
 */
const Profile: React.FC = () => {
  const { user } = useAuth()

  // Formatear nombre completo
  const fullName = useMemo(() => {
    if (!user) return 'Usuario'
    const name = `${user.first_name || ''} ${user.last_name || ''}`.trim()
    return name.replace(/\b\w/g, (l) => l.toUpperCase())
  }, [user])

  // Obtener iniciales del usuario
  const userInitials = useMemo(() => {
    if (!user) return 'U'
    const firstInitial = user.first_name?.charAt(0).toUpperCase() || ''
    const lastInitial = user.last_name?.charAt(0).toUpperCase() || ''
    return `${firstInitial}${lastInitial}` || 'U'
  }, [user])

  // Formatear rol (nombre legible del API si existe)
  const roleDisplay = useMemo(() => {
    if (!user) return 'Sin rol'
    const named = (user.role_name || user.role || '').trim()
    if (named) return named
    return `Rol ID: ${user.role_id}`
  }, [user])

  // Contar permisos
  const permissionsCount = useMemo(() => {
    if (!user) return 0
    // Si es administrador (role_id = 1), tiene todos los permisos
    if (user.role_id === ROLE_ID_ADMIN) return 'Todos los permisos'
    return user.permissions?.length || 0
  }, [user])

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <UserCircleIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">No hay información de usuario disponible</p>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
      {/* Header del perfil */}
      <div className="mb-8">
        <h2>Mi Perfil</h2>
        <p className="text-gray-600">Información de tu cuenta y permisos</p>
      </div>

      {/* Card principal con avatar y nombre */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6 mb-6 hover:shadow-md transition-shadow duration-200">
        <div className="flex items-center gap-6">
          <div className="relative group">
            <div className="h-24 w-24 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg group-hover:shadow-xl transition-all duration-200 group-hover:scale-105">
              <span className="text-2xl font-bold text-white">{userInitials}</span>
            </div>
            <div className="absolute bottom-0 right-0 h-6 w-6 bg-green-500 rounded-full border-2 border-white shadow-sm flex items-center justify-center">
              <div className="h-2 w-2 bg-white rounded-full"></div>
            </div>
          </div>
          <div className="flex-1">
            <h2 className="text-2xl font-semibold text-gray-900 mb-1">{fullName}</h2>
            <p className="text-sm text-gray-500">{user.email}</p>
            {(user.role_name || user.role) && (
              <span className="inline-block mt-2 px-3 py-1 text-xs font-medium bg-blue-50 text-blue-700 rounded-full border border-blue-100">
                {(user.role_name || user.role || '').trim()}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Grid de información */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Información personal */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-10 w-10 rounded-lg bg-blue-50 flex items-center justify-center">
              <UserCircleIcon className="h-6 w-6 text-custom-blue" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">Información Personal</h3>
          </div>
          <div className="space-y-4">
            <InfoRow
              label="Nombre"
              value={user.first_name || 'No disponible'}
              icon={UserCircleIcon}
            />
            <InfoRow
              label="Apellido"
              value={user.last_name || 'No disponible'}
              icon={UserCircleIcon}
            />
            <InfoRow
              label="Email"
              value={user.email || 'No disponible'}
              icon={EnvelopeIcon}
            />
          </div>
        </div>

        {/* Información de cuenta */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-10 w-10 rounded-lg bg-blue-50 flex items-center justify-center">
              <IdentificationIcon className="h-6 w-6 text-blue-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">Información de Cuenta</h3>
          </div>
          <div className="space-y-4">
            <InfoRow
              label="ID de Usuario"
              value={user.id.toString()}
              icon={KeyIcon}
            />
            <InfoRow
              label="ID de Agente"
              value={user.agente_id?.toString() || 'No disponible'}
              icon={BuildingOfficeIcon}
            />
            <InfoRow
              label="Rol"
              value={roleDisplay}
              icon={ShieldCheckIcon}
            />
          </div>
        </div>
      </div>

      {/* Permisos */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6 mt-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="h-10 w-10 rounded-lg bg-blue-50 flex items-center justify-center">
            <ShieldCheckIcon className="h-6 w-6 text-blue-600" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900">Permisos</h3>
            <p className="text-sm text-gray-500">
              {typeof permissionsCount === 'string'
                ? permissionsCount
                : `${permissionsCount} permiso${permissionsCount !== 1 ? 's' : ''}`}
            </p>
          </div>
        </div>
        {user.permissions && user.permissions.length > 0 && user.role_id !== ROLE_ID_ADMIN && (
          <div className="flex flex-wrap gap-2 mt-4">
            {user.permissions.map((permission, index) => (
              <span
                key={index}
                className="px-3 py-1.5 text-xs font-medium bg-blue-50 text-blue-700 rounded-lg border border-blue-100"
              >
                {permission}
              </span>
            ))}
          </div>
        )}
        {user.role_id === ROLE_ID_ADMIN && (
          <div className="mt-4">
            <span className="px-3 py-1.5 text-xs font-medium bg-green-50 text-green-700 rounded-lg border border-green-100">
              Administrador - Acceso completo
            </span>
          </div>
        )}
      </div>
    </div>
  )
}

interface InfoRowProps {
  label: string
  value: string
  icon: React.ComponentType<{ className?: string }>
}

/**
 * Componente para mostrar una fila de información
 */
const InfoRow: React.FC<InfoRowProps> = ({ label, value, icon: Icon }) => {
  return (
    <div className="flex items-start gap-3">
      <Icon className="h-5 w-5 text-gray-400 mt-0.5 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-xs text-gray-500 mb-1">{label}</p>
        <p className="text-sm font-medium text-gray-900 break-words">{value}</p>
      </div>
    </div>
  )
}

export default React.memo(Profile)
